import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RESOLUTION_OPTIONS } from '../components/charts/convergence/constants';
import type { EventModel } from './model/types';
import {
    bucketedNow,
    eventBounds,
    NOW_BUCKET_MS,
    parseRange,
    pickResolution,
} from './window';

const AVAILABLE_RESOLUTIONS = RESOLUTION_OPTIONS.map(o => o.value);

export interface EventWindow {
    bounds: [Date, Date] | null;
    range: [Date, Date] | null;
    setRange: (range: [Date, Date] | null) => void;
    fetchRange: [Date, Date] | null;
    resolution: number;
    resolutionLabel: string;
    resolutionChoice: number | null;
    setResolutionChoice: (choice: number | null) => void;
}

export function resolutionLabelOf(minutes: number): string {
    return RESOLUTION_OPTIONS.find(o => o.value === minutes)?.label ?? '';
}

export function useEventWindow(
    event: EventModel | null,
    seriesCount: number
): EventWindow {
    const [searchParams, setSearchParams] = useSearchParams();
    const [nowMs, setNowMs] = useState(() => bucketedNow());
    const [resolutionChoice, setResolutionChoice] = useState<number | null>(
        null
    );

    useEffect(() => {
        const id = setInterval(() => setNowMs(bucketedNow()), NOW_BUCKET_MS);
        return () => clearInterval(id);
    }, []);

    const eventId = event?.id ?? null;
    useEffect(() => {
        setResolutionChoice(null);
    }, [eventId]);

    const from = searchParams.get('from');
    const to = searchParams.get('to');
    const range = useMemo(() => parseRange(from, to), [from, to]);

    const setRange = useCallback(
        (next: [Date, Date] | null) => {
            setSearchParams(
                prev => {
                    const params = new URLSearchParams(prev);
                    if (next) {
                        params.set('from', next[0].toISOString());
                        params.set('to', next[1].toISOString());
                    } else {
                        params.delete('from');
                        params.delete('to');
                    }
                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const bounds = useMemo(
        () => (event ? eventBounds(event, nowMs) : null),
        [event, nowMs]
    );

    const fetchRange = range ?? bounds;
    const resolution = useMemo(
        () =>
            fetchRange
                ? pickResolution(
                      fetchRange,
                      seriesCount,
                      AVAILABLE_RESOLUTIONS,
                      resolutionChoice
                  )
                : 1440,
        [fetchRange, seriesCount, resolutionChoice]
    );

    return {
        bounds,
        range,
        setRange,
        fetchRange,
        resolution,
        resolutionLabel: resolutionLabelOf(resolution),
        resolutionChoice,
        setResolutionChoice,
    };
}
